import { AssetMetadata } from '@/asset/api/types';
import { BooleanFieldRenderer } from '@/asset/components/metadata/boolean-field-renderer';
import { DateFieldRenderer } from '@/asset/components/metadata/date-field-renderer';
import { DateTimeFieldRenderer } from '@/asset/components/metadata/date-time-field-renderer';
import { TextFieldRenderer } from '@/asset/components/metadata/text-field-renderer';
import { TimeFieldRenderer } from '@/asset/components/metadata/time-field-renderer';

export interface MetadataFieldRendererProps {
  /**
   * Metadata field to render
   */
  metadata: AssetMetadata;

  /**
   * Function to set form values
   */
  setValue: (value: string | number | boolean | null) => void;

  /**
   * Error state from form
   */
  error?: {
    message?: string;
  };

  /**
   * Index of the metadata field in the form's metadata array.
   */
  fieldIndex: number;
}

/**
 * Selects and renders the appropriate field component based on metadata type
 */
export function MetadataFieldRenderer(props: MetadataFieldRendererProps) {
  const { metadata } = props;

  switch (metadata.field.type) {
    case 'date':
      return <DateFieldRenderer {...props} />;
    case 'time':
      return <TimeFieldRenderer {...props} />;
    case 'date_time':
      return <DateTimeFieldRenderer {...props} />;
    case 'boolean':
      return <BooleanFieldRenderer {...props} />;
    case 'integer':
    case 'number':
      return (
        <TextFieldRenderer
          {...props}
          setValue={(value) => {
            if (typeof value != 'string') {
              props.setValue(value);
              return;
            }
            // If last character is a decimal point, keep this as a string
            if (value.endsWith('.')) {
              props.setValue(value);
              return;
            }
            const typedValue = Number(value);
            if (!Number.isNaN(typedValue) && Number.isFinite(typedValue)) {
              props.setValue(typedValue);
              return;
            }
            props.setValue(value);
            return;
          }}
        />
      );
    default:
      return <TextFieldRenderer {...props} />;
  }
}
